import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import LoadingOverlay from 'react-loading-overlay';
import ScoreListItem from './components/ScoreListItem';
import ProgressBar from './components/ProgressBar';
import './SkinLanding.scss';
import assessmentImage from './images';
import { commonConstant } from '../../common';
import { fetchHealthSpanProfileAction } from '../../actions/healthSpanActions';
import ConfirmButton from './components/ConfirmButton';
import { useScoreCategoryMetadata } from './useScoreCategoryMetadata';
import {
  HEALTH_SPAN_MAX_SCORE,
  SUB_CATEGORY_KEY_MAP,
  buildLandingScores,
  hasValidScore,
  getSubCategoryDisplayProps,
} from './healthSpanScore.utils';

const ASSESSMENT_IDS = {
  wrinkle: process.env.REACT_APP_SKIN_ASSESSMENT_ID || '',
  darkSpot: process.env.REACT_APP_SKIN_ASSESSMENT_ID || '',
  sagging: process.env.REACT_APP_SKIN_ASSESSMENT_ID || '',
  skinOther: process.env.REACT_APP_SKIN_ASSESSMENT_ID || '',
};

const CATEGORIES = [
  {
    key: 'darkSpot',
    image: assessmentImage.darkSpotImage,
    name: 'จุดด่างดำ',
  },
  {
    key: 'wrinkle',
    image: assessmentImage.wrinkleImage,
    name: 'ริ้วรอย',
  },
  {
    key: 'sagging',
    image: assessmentImage.saggingImage,
    name: 'หย่อนคล้อย',
  },
  {
    key: 'skinOther',
    image: assessmentImage.skinOtherImage,
    name: 'อื่นๆ',
  },
];

const SkinLanding = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { profile, loading } = useSelector((state) => state.healthSpan);
  const { metadata, metaLoading } = useScoreCategoryMetadata('skin');

  useEffect(() => {
    const wrapper = document.getElementById('wrapper');
    if (wrapper) wrapper.style.maxWidth = '100%';
    return () => {
      if (wrapper) wrapper.style.maxWidth = '';
    };
  }, []);

  useEffect(() => {
    dispatch(fetchHealthSpanProfileAction());
  }, [dispatch]);

  const { categoryScore, categoryStatus, scores } = buildLandingScores(
    profile,
    'skin',
    CATEGORIES,
    metadata
  );

  const hasValue = hasValidScore(categoryScore);
  const isLoading = loading || metaLoading;
  const tip = categoryStatus?.tip;
  const noDataColor = '#A1A1A1';
  const displayColor = hasValue ? categoryStatus.barColor : noDataColor;

  const handleCategoryClick = (category) => {
    const assessmentId = ASSESSMENT_IDS[category.key];
    if (!assessmentId) return;

    history.push(
      `${commonConstant.pathAssessmentQuestion}?assessment_id=${assessmentId}&return=${commonConstant.pathSkinAssessmentLanding}&section=${category.key}`
    );
  };

  const handleAddUpdateClick = () => {
    const assessmentId = process.env.REACT_APP_SKIN_ASSESSMENT_ID;
    history.push(
      `${commonConstant.pathHnWSkinAssessment}?assessment_id=${assessmentId}&return=${encodeURIComponent(commonConstant.pathSkinAssessmentLanding)}`
    );
  };

  const headerScore = hasValue ? categoryScore : '-';
  const headerMaxScore = categoryStatus?.maxScore || HEALTH_SPAN_MAX_SCORE;

  return (
    <div className="skin-landing-component">
      {isLoading && (
        <LoadingOverlay
          text="Loading"
          spinner
          active={true}
          styles={{ wrapper: { width: '100%', height: '100vh', position: 'fixed', top: 0, left: 0, zIndex: 10 } }}
        >
          <div />
        </LoadingOverlay>
      )}

      {!isLoading && (
      <>
      <div className="bg-detail-container">
        <img src={assessmentImage.landingBgDetail} alt="" className="bg-detail" />
      </div>

      <div className="content-container">
        <div className="header-section">
          <div className="image-component">
            <img src={assessmentImage.skinImage} alt="" />
          </div>
          <div className="detail" style={{ color: displayColor }}>
            <div className="header-name">{metadata?.display_name || 'สุขภาพผิว'}</div>
            <div className="status">{hasValue ? categoryStatus.statusText : 'ไม่มีข้อมูล'}</div>
            <div className="score-container">
              <div className="value">{headerScore}</div>
              <div className="max-value">/{headerMaxScore}</div>
            </div>
            <ProgressBar
              value={hasValue ? categoryScore : 0}
              maxValue={headerMaxScore}
              width="130px"
              barColor={displayColor}
            />
          </div>
        </div>

        {hasValue && tip && (
          <div className="tip-card">
            <div className="tip-label-container">
              <img src={assessmentImage.tipIcon} alt="" className="tip-label-image" />
              <div className="tip-label-text">คำแนะนำ</div>
            </div>
            <div className="tip-detail-container">
              <img src={tip.image || assessmentImage.tipSkinImage} alt="" className="image" />
              <div className="text-container">
                <div className="tip-text">{tip.title}</div>
                <div className="tip-detail-text">{tip.description}</div>
              </div>
            </div>
          </div>
        )}

        <div className="summary-score-list">
          <div className="title">ข้อมูลสุขภาพผิว</div>

          {CATEGORIES.map((category) => {
            const scoreData = scores[category.key] || {};
            const hasData = hasValidScore(scoreData.score);
            const apiKey = SUB_CATEGORY_KEY_MAP[category.key];
            const subName =
              metadata?.sub_categories?.[apiKey]?.display_name || category.name;
            const display = getSubCategoryDisplayProps(scoreData, hasData);

            return (
              <ScoreListItem
                key={category.key}
                image={category.image}
                name={subName}
                statusText={display.statusText}
                statusColor={display.barColor}
                value={hasData ? scoreData.score : '-'}
                maxValue={scoreData.maxScore || 0}
                barColor={display.barColor}
                valueColor={display.valueColor}
                hasData={hasData}
                clickable={false}
              />
            );
          })}
        </div>

        <div className="description-card">
          <div className="title">สุขภาพผิว (Skin Health)</div>
          <div className="description-text">
            มิตินี้ประเมิน
            <span className="highlight">สุขภาพผิวจากภายใน </span>
            ทั้งความชุ่มชื้น ความแข็งแรงของเกราะป้องกันผิว และปัจจัยด้านไลฟ์สไตล์ที่อาจส่งผลต่อสภาพผิว
          </div>
        </div>
      </div>
      </>
      )}

      <div className="footer-container">
        <ConfirmButton text={hasValue ? 'อัปเดทข้อมูล' : 'เพิ่มข้อมูล'} width={'100%'} color={'black'} onClick={handleAddUpdateClick} />
      </div>
    </div>
  );
};

export default SkinLanding;
